import React from 'react'

/**
 * Jak działa instalacja: diagram przepływu energii dla klienta (decyzja właściciela 2026-10-11).
 * Bez zabezpieczeń, kabli i oznaczeń elektrycznych: słońce, moduły, falownik, a z falownika dom, magazyn
 * i sieć z licznikiem dwukierunkowym. Na strzałkach roczne ilości z prognozy; grubość strzałki rośnie
 * z ilością energii. Kolor firmy przez zmienne CSS dokumentu (`--vv-dok`). Pełny schemat jednokreskowy
 * jest w zgłoszeniu do operatora; dobór łańcuchów z paskami napięć i prądów stoi pod diagramem.
 */

const kwh = (v) => `${Math.round(v).toLocaleString('pl-PL')} kWh`
const AKCENT = 'var(--vv-dok, #2563EB)'
const AKCENT_JASNY = 'var(--vv-dok-jasny, #EFF6FF)'
const SZARY = '#9ca3af'

const grubosc = (wartosc, max) => (max > 0 && wartosc > 0 ? 3 + 7 * Math.min(1, wartosc / max) : 3)

const Strzalka = ({ d, szer, kolor = AKCENT, przerywana = false, id }) => (
  <g>
    <defs>
      {/* Grot w jednostkach rysunku, nie linii: przy grubej strzałce nie rośnie i nie zasłania liczb. */}
      <marker id={id} viewBox="0 0 10 10" refX="6" refY="5" markerWidth="15" markerHeight="15" markerUnits="userSpaceOnUse" orient="auto-start-reverse">
        <path d="M0,0 L10,5 L0,10 z" fill={kolor} />
      </marker>
    </defs>
    <path d={d} fill="none" stroke={kolor} strokeWidth={szer} strokeLinecap="round" strokeOpacity={przerywana ? 0.8 : 0.9}
          strokeDasharray={przerywana ? '7 7' : undefined} markerEnd={`url(#${id})`} />
  </g>
)

const Etykieta = ({ x, y, tytul, wartosc, wyrownanie = 'middle' }) => (
  <g>
    {wartosc && <text x={x} y={y} textAnchor={wyrownanie} fontSize="13" fontWeight="700" fill="#111827">{wartosc}</text>}
    <text x={x} y={y + (wartosc ? 15 : 0)} textAnchor={wyrownanie} fontSize="11" fill="#6b7280">{tytul}</text>
  </g>
)

const Kafel = ({ x, y, w, h, children }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx="14" fill={AKCENT_JASNY} stroke={AKCENT} strokeOpacity="0.35" />
    {children}
  </g>
)

const DiagramEnergii = ({ liczbaModulow, mocModuluW, kwp, nazwaFalownika, mocFalownikaKw, magazynKwh = 0, rocznie = null }) => {
  const produkcja = rocznie?.produkcjaKwh || 0
  const doDomu = rocznie?.autokonsumpcjaKwh
  const doSieci = rocznie?.nadwyzkaKwh
  const zSieci = rocznie?.zSieciKwh
  const zLiczbami = doDomu != null
  const max = produkcja
  const kwpTekst = kwp ? `${String(Math.round(kwp * 100) / 100).replace('.', ',')} kWp` : ''

  return (
    <svg viewBox="0 0 790 330" className="w-full" role="img" aria-label="Przepływ energii w instalacji: słońce, moduły, falownik, dom, magazyn i sieć">
      {/* Słońce */}
      <g transform="translate(52 88)">
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i * Math.PI) / 4
          return <line key={i} x1={Math.cos(a) * 30} y1={Math.sin(a) * 30} x2={Math.cos(a) * 40} y2={Math.sin(a) * 40} stroke="#f59e0b" strokeWidth="4" strokeLinecap="round" />
        })}
        <circle r="22" fill="#fbbf24" />
      </g>
      <Etykieta x={52} y={152} tytul="słońce" />
      <Strzalka id="s-slonce" d="M 98 88 L 132 88" szer={4} kolor="#f59e0b" przerywana />

      {/* Moduły */}
      <Kafel x={140} y={40} w={150} h={96}>
        <g transform="translate(165 58)">
          {Array.from({ length: 3 }, (_, r) => Array.from({ length: 4 }, (__, k) => (
            <rect key={`${r}${k}`} x={k * 25} y={r * 18} width="23" height="16" rx="2" fill="#1e293b" />
          )))}
        </g>
      </Kafel>
      <Etykieta x={215} y={156} wartosc={liczbaModulow && mocModuluW ? `${liczbaModulow} × ${mocModuluW} W` : kwpTekst} tytul={`moduły na dachu${liczbaModulow && mocModuluW ? `, ${kwpTekst}` : ''}`} />

      <Strzalka id="s-moduly" d="M 292 88 L 342 88" szer={grubosc(produkcja, max)} />
      {produkcja > 0 && <text x={317} y={66} textAnchor="middle" fontSize="11" fontWeight="600" fill="#374151">{kwh(produkcja)}</text>}

      {/* Falownik */}
      <Kafel x={350} y={46} w={104} h={84}>
        <rect x={378} y={60} width="48" height="44" rx="6" fill="#ffffff" stroke={AKCENT} strokeOpacity="0.6" />
        <path d="M 386 76 q 8 -10 16 0 t 16 0" fill="none" stroke={AKCENT} strokeWidth="3" strokeLinecap="round" />
        <line x1={386} y1={92} x2={418} y2={92} stroke={AKCENT} strokeWidth="3" strokeLinecap="round" />
      </Kafel>
      <Etykieta x={402} y={150} wartosc={mocFalownikaKw ? `${String(mocFalownikaKw).replace('.', ',')} kW` : null} tytul="falownik zamienia prąd na domowy" />
      {nazwaFalownika && <text x={402} y={180} textAnchor="middle" fontSize="10" fill="#9ca3af">{String(nazwaFalownika).slice(0, 34)}</text>}

      {/* Dom */}
      <Kafel x={560} y={14} w={180} h={80}>
        <path d="M 586 64 L 608 44 L 630 64 L 630 82 L 586 82 Z" fill="#ffffff" stroke={AKCENT} strokeWidth="2" strokeLinejoin="round" />
        <rect x={602} y={68} width="12" height="14" fill={AKCENT} fillOpacity="0.5" />
        <Etykieta x={645} y={46} wyrownanie="start" wartosc={zLiczbami ? kwh(doDomu) : null} tytul="zużyte w domu" />
      </Kafel>
      <Strzalka id="s-dom" d="M 456 76 C 510 76, 510 54, 552 54" szer={grubosc(doDomu ?? produkcja / 2, max)} />

      {/* Magazyn */}
      {magazynKwh > 0 && (
        <>
          <Kafel x={560} y={118} w={180} h={70}>
            <rect x={586} y={136} width="36" height="34" rx="5" fill="#ffffff" stroke={AKCENT} strokeWidth="2" />
            <rect x={597} y={131} width="14" height="5" rx="1.5" fill={AKCENT} />
            <rect x={591} y={152} width="26" height="14" rx="2" fill={AKCENT} fillOpacity="0.55" />
            <Etykieta x={645} y={146} wyrownanie="start" wartosc={`${String(magazynKwh).replace('.', ',')} kWh`} tytul="magazyn na wieczór" />
          </Kafel>
          <Strzalka id="s-mag" d="M 456 88 C 510 88, 510 153, 552 153" szer={5} />
        </>
      )}

      {/* Sieć z licznikiem dwukierunkowym */}
      <Kafel x={560} y={214} w={180} h={86}>
        <g transform="translate(586 230)" stroke={SZARY} strokeWidth="2.5" fill="none" strokeLinecap="round">
          <path d="M 18 0 L 6 48 M 18 0 L 30 48 M 9 18 L 27 18 M 7 32 L 29 32" />
        </g>
        <Etykieta x={645} y={244} wyrownanie="start" wartosc={zLiczbami ? kwh(doSieci) : null} tytul="oddane do sieci" />
        {zLiczbami && <Etykieta x={645} y={278} wyrownanie="start" wartosc={kwh(zSieci)} tytul="pobrane z sieci" />}
      </Kafel>
      <Strzalka id="s-siec" d="M 456 104 C 510 104, 510 246, 552 246" szer={grubosc(doSieci ?? produkcja / 3, max)} />
      {/* Prąd z sieci wraca do domu, gdy słońca i magazynu brakuje */}
      <Strzalka id="s-zsieci" d="M 744 258 C 776 230, 776 84, 748 58" szer={3} kolor={SZARY} przerywana />

      <text x={20} y={318} fontSize="11" fill="#6b7280">
        W dzień prąd z dachu najpierw zasila dom; nadwyżka {magazynKwh > 0 ? 'ładuje magazyn albo ' : ''}trafia do sieci. Wieczorem i w nocy dom bierze prąd {magazynKwh > 0 ? 'z magazynu, a gdy go brakuje, ' : ''}z sieci.
      </text>
    </svg>
  )
}

export default DiagramEnergii
